import { Card, CardContent } from "@exhousing/design-system";
import { Crown, Home, MapPin, MessageSquareQuote, Users } from "lucide-react";
import { Link } from "react-router-dom";

import { StatusBadge } from "@/components/StatusBadge";
import { useApplications } from "@/hooks/api/useApplications";
import { useProperties } from "@/hooks/api/useProperties";

function eur(value: string | number | null | undefined): string {
  if (value == null) return "—";
  return `€ ${Number(value).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}`;
}

/**
 * Journey 1.1: the landlord's calm home — his objects as cards with the funnel state
 * and candidate counts, and the quiet empty state while the agency sets things up.
 */
export function LandlordDashboard({ firstName }: { firstName: string }) {
  const { data: properties } = useProperties();
  const { data: applications } = useApplications();

  const candidateCount = (propertyId: string) =>
    (applications ?? []).filter(
      (a) =>
        a.property_id === propertyId &&
        !["cancelled", "rejected", "tenant"].includes(a.status),
    ).length;

  // Where Marc actually has a move: finalists waiting for his selection, or an
  // option already out. One card per property, straight to the comparison table.
  const STAGE_COPY: Record<string, string> = {
    approved: "awaiting your selection",
    reserve: "awaiting your selection",
    under_option: "offer out — waiting for the candidate",
  };
  const decisions = (properties ?? [])
    .map((p) => {
      const finalists = (applications ?? []).filter(
        (a) => a.property_id === p.id && a.status in STAGE_COPY,
      );
      const selectable = finalists.filter((a) =>
        ["approved", "reserve"].includes(a.status),
      ).length;
      const optionOut = finalists.some((a) => a.status === "under_option");
      return { property: p, selectable, optionOut };
    })
    .filter((d) => d.selectable > 0 || d.optionOut || d.property.selection_advice);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">
          Welcome, {firstName}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your properties, candidates and contracts — managed by your agency,
          followed by you.
        </p>
      </header>

      {decisions.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Needs your attention
          </h2>
          {decisions.map(({ property: p, selectable, optionOut }) => (
            <Link key={p.id} to={`/applications?property=${p.id}`}>
              <Card className="border-primary/40 bg-primary/[0.03] transition-colors hover:border-primary">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-5">
                  <div className="min-w-0">
                    <p className="font-medium">
                      {[p.street, p.house_number].filter(Boolean).join(" ") || p.city}
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {optionOut
                        ? "Offer sent — waiting for the candidate's decision."
                        : `${selectable} approved candidate${selectable === 1 ? "" : "s"} awaiting your selection.`}
                      {p.selection_advice
                        ? " Your agency's advice is ready."
                        : ""}
                    </p>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-primary">
                    {p.selection_advice ? (
                      <MessageSquareQuote className="size-4" />
                    ) : (
                      <Crown className="size-4" />
                    )}
                    {optionOut ? "View status" : "Review & select"}
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {properties && properties.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {properties.map((p) => {
            const candidates = candidateCount(p.id);
            return (
              <Link key={p.id} to={`/properties/${p.id}`}>
                <Card className="h-full transition-colors hover:border-primary">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-2">
                      <p className="flex items-center gap-1.5 font-medium">
                        <MapPin className="size-4 shrink-0 text-muted-foreground" />
                        {[p.street, p.house_number].filter(Boolean).join(" ") ||
                          p.city}
                      </p>
                      <StatusBadge status={p.rental_status} />
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{p.city}</p>
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <span className="font-semibold">
                        {eur(p.advertised_rent)}{" "}
                        <span className="font-normal text-muted-foreground">
                          / month
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Users className="size-4" />
                        {candidates} candidate{candidates === 1 ? "" : "s"}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <Home className="size-10 text-muted-foreground/50" />
            <h2 className="mt-4 font-display text-xl font-medium">
              No properties yet
            </h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Your agency manages your properties — they appear here as soon as
              they are set up.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
